import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { clearAuthCredentials } from "../api/annaCallRobotApi";
import { annaCallRobotClient } from "../api/annaCallRobotClient";
import { DEFAULT_APP_TIMEZONE } from "../utils/timezoneOptions";
import type {
  AuthState,
  AuthUser,
  SiteDataContextValue,
  SiteDataProviderProps,
} from "./SiteDataContextTypes";

const SiteDataContext = createContext<SiteDataContextValue | undefined>(
  undefined,
);

const STORAGE_KEY_USE_24_HOUR = "app_use_24_hour_time";
const STORAGE_KEY_SHOW_WEBHOOK_LOGS_TAB = "app_show_webhook_logs_tab";
const STORAGE_KEY_SHOW_DNC_CHECK_TAB = "app_show_dnc_check_tab";
const STORAGE_KEY_SHOW_REPORTS_TABS = "app_show_reports_tabs";
const STORAGE_KEY_SHOW_VAPI_EMULATOR_TAB = "app_show_vapi_emulator_tab";
const LEGACY_STORAGE_KEY_SHOW_VAPI_EMULATOR_ANALYTICS_BUTTON =
  "app_show_vapi_emulator_analytics_button";

function readStoredUse24HourTimeFormat(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_USE_24_HOUR);
    if (stored === null) {
      return true;
    }
    return stored === "1";
  } catch {
    return true;
  }
}

function readStoredShowWebhookLogsTab(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_SHOW_WEBHOOK_LOGS_TAB) === "1";
  } catch {
    return false;
  }
}

/** When the key is absent, show the tab (existing behavior before this setting existed). */
function readStoredShowReportsTabs(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY_SHOW_REPORTS_TABS) === "1";
  } catch {
    return false;
  }
}

function readStoredShowDncCheckTab(): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY_SHOW_DNC_CHECK_TAB);
    if (v === null) {
      return true;
    }
    return v === "1";
  } catch {
    return true;
  }
}

function readStoredShowVapiEmulatorTab(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SHOW_VAPI_EMULATOR_TAB);
    if (stored !== null) {
      return stored === "1";
    }
    return (
      localStorage.getItem(
        LEGACY_STORAGE_KEY_SHOW_VAPI_EMULATOR_ANALYTICS_BUTTON,
      ) === "1"
    );
  } catch {
    return false;
  }
}

export const checkIsAuthenticated = (): boolean => {
  const token = localStorage.getItem("auth_token");
  const username = localStorage.getItem("auth_username");
  return !!(token && username);
};

export const getAuthUser = (): AuthUser | null => {
  if (checkIsAuthenticated()) {
    return {
      username: localStorage.getItem("auth_username") as string,
      token: localStorage.getItem("auth_token") as string,
    };
  }
  return null;
};

export const validateToken = async (): Promise<boolean> => {
  const token = localStorage.getItem("auth_token");

  if (!token) {
    return false;
  }

  try {
    const response = await annaCallRobotClient.getSiteTexts({
      skip401Redirect: true,
    });

    const isTokenValid = response.status === 200;
    return isTokenValid;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number }; message?: string };
    if (err.response?.status === 401) {
      return false;
    }

    console.warn(
      "Token validation failed due to network or server error:",
      err.message,
    );
    return true;
  }
};

export const logout = (): void => {
  clearAuthCredentials();
  window.location.reload();
};

export const SiteDataProvider = ({
  children,
  siteData,
  setSiteData,
}: SiteDataProviderProps) => {
  const [use24HourTimeFormat, setUse24HourTimeFormatState] = useState(
    readStoredUse24HourTimeFormat,
  );
  const setUse24HourTimeFormat = useCallback((next: boolean) => {
    const v = Boolean(next);
    setUse24HourTimeFormatState(v);
    try {
      localStorage.setItem(STORAGE_KEY_USE_24_HOUR, v ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const [showWebhookLogsTab, setShowWebhookLogsTabState] = useState(
    readStoredShowWebhookLogsTab,
  );
  const setShowWebhookLogsTab = useCallback((next: boolean) => {
    const v = Boolean(next);
    setShowWebhookLogsTabState(v);
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_WEBHOOK_LOGS_TAB, v ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const [showDncCheckTab, setShowDncCheckTabState] = useState(
    readStoredShowDncCheckTab,
  );
  const setShowDncCheckTab = useCallback((next: boolean) => {
    const v = Boolean(next);
    setShowDncCheckTabState(v);
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_DNC_CHECK_TAB, v ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const [showReportsTabs, setShowReportsTabsState] = useState(
    readStoredShowReportsTabs,
  );
  const setShowReportsTabs = useCallback((next: boolean) => {
    const v = Boolean(next);
    setShowReportsTabsState(v);
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_REPORTS_TABS, v ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const [simulateVapiCalls, setSimulateVapiCallsState] = useState(false);
  const setSimulateVapiCalls = useCallback((next: boolean) => {
    setSimulateVapiCallsState(Boolean(next));
  }, []);

  const [showVapiEmulatorTab, setShowVapiEmulatorTabState] = useState(
    readStoredShowVapiEmulatorTab,
  );
  const setShowVapiEmulatorTab = useCallback((next: boolean) => {
    const v = Boolean(next);
    setShowVapiEmulatorTabState(v);
    try {
      localStorage.setItem(STORAGE_KEY_SHOW_VAPI_EMULATOR_TAB, v ? "1" : "0");
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  const [appDisplayTimezone, setAppDisplayTimezone] =
    useState(DEFAULT_APP_TIMEZONE);
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    isLoading: true,
  });

  const refreshAuthState = async (): Promise<void> => {
    const isAuth = checkIsAuthenticated();
    const user = getAuthUser();

    setAuthState({
      isAuthenticated: isAuth,
      user: user,
      isLoading: false,
    });
  };

  useEffect(() => {
    void refreshAuthState();
  }, []);

  useEffect(() => {
    if (!authState.isAuthenticated) {
      return;
    }
    const token = localStorage.getItem("auth_token");
    if (!token) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const response = await annaCallRobotClient.getAppSettings();
        const data = response.data ?? {};
        const tz = data.timezone;
        if (!cancelled && typeof tz === "string" && tz.trim()) {
          setAppDisplayTimezone(tz.trim());
        }
        if (!cancelled) {
          setSimulateVapiCallsState(Boolean(data.simulate_calls));
        }
      } catch {
        /* keep default */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authState.isAuthenticated]);

  useEffect(() => {
    const handleFocus = () => {
      void refreshAuthState();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  const contextValue: SiteDataContextValue = {
    siteData,
    setSiteData,
    authState,
    setAuthState,
    refreshAuthState,
    checkIsAuthenticated,
    getAuthUser,
    validateToken,
    logout,
    appDisplayTimezone,
    setAppDisplayTimezone,
    use24HourTimeFormat,
    setUse24HourTimeFormat,
    showWebhookLogsTab,
    setShowWebhookLogsTab,
    showDncCheckTab,
    setShowDncCheckTab,
    showReportsTabs,
    setShowReportsTabs,
    simulateVapiCalls,
    setSimulateVapiCalls,
    showVapiEmulatorTab,
    setShowVapiEmulatorTab,
  };

  return (
    <SiteDataContext.Provider value={contextValue}>
      {children}
    </SiteDataContext.Provider>
  );
};

export function useSiteData(options: {
  optional: true;
}): SiteDataContextValue | undefined;
export function useSiteData(options?: {
  optional?: false;
}): SiteDataContextValue;
export function useSiteData(options?: {
  optional?: boolean;
}): SiteDataContextValue | undefined {
  const ctx = useContext(SiteDataContext);
  if (options?.optional) {
    return ctx;
  }
  if (ctx === undefined) {
    throw new Error("useSiteData must be used within a SiteDataProvider");
  }
  return ctx;
}
